import React, { useState, useRef, useEffect } from 'react';
import { useAssistant } from '../../hooks/useAssistant';
import { X, Send, MessageSquare, Trash2, Sparkles } from 'lucide-react';

const AssistantChat = ({ isOpen, onClose }) => {
  const [pregunta, setPregunta] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const { chatHistory, enviarPregunta, limpiarChat } = useAssistant();
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!pregunta.trim()) return;

    enviarPregunta(pregunta);
    setPregunta('');
  };

  const preguntasSugeridas = [
    'Resumen del mes',
    '¿Cuántas garantías activas hay?',
    '¿Cuál es el estado de las averías?',
    'Muéstrame la facturación',
    '¿Cuál es el ratio de averías?'
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className={`bg-white rounded-lg shadow-2xl border-2 border-purple-200 transition-all duration-300 ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
      }`}>
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between cursor-pointer"
             onClick={() => setIsMinimized(!isMinimized)}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <div>
              <h3 className="font-bold">Asistente EUREKA</h3>
              <p className="text-xs opacity-90">Chat simulado - Prototipo</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isMinimized && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  limpiarChat();
                }}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Limpiar chat"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            <div className="h-[440px] overflow-y-auto p-4 space-y-3 bg-gray-50">
              {chatHistory.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm mb-4">
                    ¡Hola! Soy el asistente EUREKA. Pregúntame sobre:
                  </p>
                  <div className="space-y-2">
                    {preguntasSugeridas.map((sugerencia, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setPregunta(sugerencia);
                          inputRef.current?.focus();
                        }}
                        className="block w-full text-left px-3 py-2 text-sm bg-white hover:bg-purple-50 border border-gray-200 rounded-lg transition-colors"
                      >
                        💡 {sugerencia}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {chatHistory.map((mensaje) => (
                    <div
                      key={mensaje.id}
                      className={`flex ${mensaje.tipo === 'usuario' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          mensaje.tipo === 'usuario'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white border border-gray-200 text-gray-800'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-line">
                          {mensaje.mensaje}
                        </div>
                        <div className={`text-xs mt-1 ${
                          mensaje.tipo === 'usuario' ? 'text-purple-200' : 'text-gray-400'
                        }`}>
                          {mensaje.timestamp.toLocaleTimeString('es-ES', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={pregunta}
                  onChange={(e) => setPregunta(e.target.value)}
                  placeholder="Escribe tu pregunta..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={!pregunta.trim()}
                  className="bg-purple-600 text-white p-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="h-5 w-5" />
                </button>
              </form>
              <p className="text-xs text-gray-400 mt-2 text-center">
                ⚠️ Respuestas simuladas - Prototipo sin IA real
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssistantChat;
